import { c as _c } from "react/compiler-runtime";
import { Center, Spinner } from '@chakra-ui/react';
import { AdvisorChatAside } from '@src/components/sco/Advisors/layout/AdvisorChatAside';
import { AdvisorsGuestNav } from '@src/components/sco/Advisors/layout/AdvisorsGuestNav';
import { AdvisorsMainPanel } from '@src/components/sco/Advisors/layout/AdvisorsMainPanel';
import { AdvisorsNav } from '@src/components/sco/Advisors/layout/AdvisorsNav';
import { AdvisorsToolbarExtrasProvider } from '@src/components/sco/Advisors/layout/AdvisorsToolbarExtras';
import { AdvisorsLandingSection } from '@src/components/sco/Advisors/sections/AdvisorsLandingSection';
import { advisorsGuestRoute, advisorsRouteServerRenders } from '@src/components/sco/Advisors/utils/advisorsGuestRoutes';
import { AppLayout, attachedAppLayoutPanelProps } from '@src/components/sco/AppLayout';
import { ScoSiteNavBar } from '@src/components/sco/nav/ScoSiteNavBar';
import { HubspotHideChatStyles } from '@src/components/sco/utilities/HubspotChatHandler/HubspotHideChatStyles';
import { SSRGate } from '@src/components/sco/utilities/SSRGate';
import { BreadcrumbItem } from '@src/contexts/ActiveContentContext';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useRouter } from 'next/router';
import { ReactNode } from 'react';
type AdvisorsLayoutProps = {
  breadcrumbs?: BreadcrumbItem[];
  children: ReactNode;
  showAside?: boolean;
  /**
   * Nest inside parent chrome (e.g. Request Workspace) without site header.
   * Fills the parent panel instead of using 100vh.
   */
  embedded?: boolean;
  /** Hide hub nav (live session room). */
  focusMode?: boolean;
};

/**
 * Advisors marketing landing in a single-panel AppLayout.
 * Logged-out visitors see it on `/advisors` and on gated `/advisors/**` routes.
 * `/advisors/landing` always mounts this layout. `/advisors/v2` 301s to `/advisors`.
 *
 * `serverRender`: `/advisors` for guests, and `/advisors/landing`. On gated routes it stays
 * client-only so those URLs never serve a duplicate of the landing to crawlers.
 */
export const AdvisorsLandingLayout = t0 => {
  const $ = _c(5);
  const {
    serverRender: t1
  } = t0;
  const serverRender = t1 === undefined ? false : t1;
  const t2 = !serverRender;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <HubspotHideChatStyles />;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ScoSiteNavBar />;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <AppLayout header={t4} id="advisors-landing" panels={[{
      id: "advisors-landing-main",
      children: <AdvisorsLandingSection />,
      containerProps: {
        bgColor: "colorPalette.panel"
      }
    }]} />;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== t2) {
    t6 = <SSRGate disableSSR={t2}>{t3}{t5}</SSRGate>;
    $[3] = t2;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  return t6;
};
export const AdvisorsLayout = t0 => {
  const $ = _c(51);
  const {
    breadcrumbs,
    children,
    showAside,
    embedded: t1,
    focusMode: t2
  } = t0;
  const embedded = t1 === undefined ? false : t1;
  const focusMode = t2 === undefined ? false : t2;
  const {
    currentUser,
    currentUserLoading
  } = useCurrentUser();
  const router = useRouter();
  const waitingForAuth = currentUserLoading && !currentUser;
  const isLoggedOut = !currentUser || !!currentUser.isGuest;
  let guestRoute;
  let t3;
  if ($[0] !== embedded || $[1] !== isLoggedOut || $[2] !== router) {
    guestRoute = embedded ? null : advisorsGuestRoute(router.asPath || "");
    t3 = advisorsRouteServerRenders(guestRoute, isLoggedOut);
    $[0] = embedded;
    $[1] = isLoggedOut;
    $[2] = router;
    $[3] = guestRoute;
    $[4] = t3;
  } else {
    guestRoute = $[3];
    t3 = $[4];
  }
  const disableSSR = !t3;
  if (waitingForAuth) {
    let t4;
    if ($[5] !== embedded) {
      t4 = !embedded && <HubspotHideChatStyles />;
      $[5] = embedded;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== embedded) {
      t5 = !embedded && <ScoSiteNavBar />;
      $[7] = embedded;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    const t6 = embedded ? "full" : undefined;
    const t7 = embedded ? "full" : "70vh";
    let t8;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Spinner colorPalette="orange" size="lg" />;
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    let t9;
    if ($[10] !== t6 || $[11] !== t7) {
      t9 = <Center bg="bg" height={t6} minHeight={t7}>{t8}</Center>;
      $[10] = t6;
      $[11] = t7;
      $[12] = t9;
    } else {
      t9 = $[12];
    }
    let t10;
    if ($[13] !== disableSSR || $[14] !== t4 || $[15] !== t5 || $[16] !== t9) {
      t10 = <SSRGate disableSSR={disableSSR}>{t4}{t5}{t9}</SSRGate>;
      $[13] = disableSSR;
      $[14] = t4;
      $[15] = t5;
      $[16] = t9;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    return t10;
  }
  if (isLoggedOut) {
    if (embedded) {
      let t4;
      if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <SSRGate disableSSR={true}><Center bg="bg" height="full" minHeight="full" padding={8}><Spinner colorPalette="orange" size="lg" /></Center></SSRGate>;
        $[18] = t4;
      } else {
        t4 = $[18];
      }
      return t4;
    }
    if (!guestRoute || guestRoute === "landing") {
      const t4 = guestRoute === "landing";
      let t5;
      if ($[19] !== t4) {
        t5 = <AdvisorsLandingLayout serverRender={t4} />;
        $[19] = t4;
        $[20] = t5;
      } else {
        t5 = $[20];
      }
      return t5;
    }
  }
  let t4;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      overflow: "hidden"
    };
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] !== breadcrumbs || $[23] !== children || $[24] !== focusMode || $[25] !== isLoggedOut || $[26] !== showAside) {
    t5 = focusMode ? children : <AdvisorsMainPanel breadcrumbs={breadcrumbs} showAside={showAside && !isLoggedOut}>{children}</AdvisorsMainPanel>;
    $[22] = breadcrumbs;
    $[23] = children;
    $[24] = focusMode;
    $[25] = isLoggedOut;
    $[26] = showAside;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  let t6;
  if ($[28] !== t5) {
    t6 = {
      id: "advisors-main",
      containerProps: t4,
      children: t5
    };
    $[28] = t5;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  const mainPanel = t6;
  let t7;
  if ($[30] !== focusMode || $[31] !== isLoggedOut || $[32] !== mainPanel || $[33] !== showAside) {
    t7 = focusMode ? [mainPanel] : [{
      id: "advisors-nav",
      children: isLoggedOut ? <AdvisorsGuestNav /> : <AdvisorsNav />,
      defaultWidth: 220,
      resizeHandlePosition: "right" as const,
      isResizable: true,
      useDrawerBreakpoints: {
        base: true,
        md: false
      },
      containerProps: {
        minHeight: 0,
        overflow: "hidden"
      }
    }, mainPanel, ...(showAside && !isLoggedOut ? [{
      id: "advisors-aside",
      children: <AdvisorChatAside />,
      defaultWidth: 340,
      resizeHandlePosition: "left" as const,
      isResizable: true,
      useDrawerBreakpoints: {
        base: true,
        lg: false
      }
    }] : [])];
    $[30] = focusMode;
    $[31] = isLoggedOut;
    $[32] = mainPanel;
    $[33] = showAside;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  const panels = t7;
  let t8;
  if ($[35] !== embedded) {
    t8 = !embedded && <HubspotHideChatStyles />;
    $[35] = embedded;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== embedded) {
    t9 = embedded ? null : <ScoSiteNavBar />;
    $[37] = embedded;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  const t10 = embedded ? "advisors-embedded" : "advisors";
  const t11 = embedded ? attachedAppLayoutPanelProps : undefined;
  let t12;
  if ($[39] !== embedded) {
    t12 = embedded ? {
      height: "full",
      maxHeight: "100%",
      minHeight: 0
    } : undefined;
    $[39] = embedded;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== panels || $[42] !== t10 || $[43] !== t11 || $[44] !== t12 || $[45] !== t9) {
    t13 = <AdvisorsToolbarExtrasProvider><AppLayout header={t9} id={t10} panelProps={t11} panels={panels} containerProps={t12} /></AdvisorsToolbarExtrasProvider>;
    $[41] = panels;
    $[42] = t10;
    $[43] = t11;
    $[44] = t12;
    $[45] = t9;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  let t14;
  if ($[47] !== disableSSR || $[48] !== t13 || $[49] !== t8) {
    t14 = <SSRGate disableSSR={disableSSR}>{t8}{t13}</SSRGate>;
    $[47] = disableSSR;
    $[48] = t13;
    $[49] = t8;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  return t14;
};